"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import { DownopenArrowIcon, LogOutIcon, UserIcon } from "@/components/icons";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { cn } from "@/src/lib/utils";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from "@/api/base/private/axiosPrivateClient";
import type { UserEntity } from "@/types/canonical";
import { NavUserProfileDrawer } from "@/components/nav-user/NavUserProfileDrawer";

const ASSIGNMENTS_TAB_STORAGE_KEY = "assignments-active-tab";

export function NavUser({ user }: { user: UserEntity | null }) {
  const t = useTranslations();
  const { isMobile } = useSidebar();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "—";
  const displayEmail = user?.email || "—";
  const avatarUrl =
    user?.photo_thumb?.medium || user?.photo_thumb?.small || user?.photo || "";

  const handleLogout = () => {
    if (typeof window === "undefined") return;

    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(ASSIGNMENTS_TAB_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear localStorage tokens", error);
    }

    try {
      const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = `${ACCESS_TOKEN_KEY}=; expires=${expire}; path=/`;
      document.cookie = `${REFRESH_TOKEN_KEY}=; expires=${expire}; path=/`;
    } catch (error) {
      console.error("Failed to clear cookies", error);
    }

    window.location.href = "/auth";
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem className="w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label="Открыть меню пользователя"
              className={cn(
                "rounded-lg border border-transparent shadow-sm",
                "bg-[linear-gradient(90deg,rgba(0,89,255,0.15)_0%,rgba(178,11,255,0.15)_100%)]",
                "hover:opacity-90",
                "transition-opacity"
              )}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback className="rounded-lg">
                  {displayName?.slice(0, 1)}
                </AvatarFallback>
              </Avatar>

              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs opacity-80">{displayEmail}</span>
              </div>

              <DownopenArrowIcon size={16} className="ms-auto opacity-70" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg p-2"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={6}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2 py-2 text-start text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="rounded-lg">
                    {displayName?.slice(0, 1)}
                  </AvatarFallback>
                </Avatar>

                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  <span className="truncate text-xs opacity-80">{displayEmail}</span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="gap-2"
              onSelect={() => setIsProfileOpen(true)}
            >
              <UserIcon size={16} className="text-muted-foreground" />
              {t("label-profile")}
            </DropdownMenuItem>

            <DropdownMenuItem
              className="mt-1 gap-2 bg-gradient-to-r from-[#0059FF] to-[#B00CFF] text-white focus:bg-gradient-to-r focus:from-[#0059FF] focus:to-[#B00CFF] focus:text-white hover:opacity-90"
              onSelect={(event) => {
                event.preventDefault();
                handleLogout();
              }}
            >
              <LogOutIcon size={16} className="text-white" />
              {t("btn-logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <NavUserProfileDrawer
          user={user}
          open={isProfileOpen}
          onOpenChange={setIsProfileOpen}
          onLogout={handleLogout}
        />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
